import { Box, Button, Typography } from "@mui/material";
import { AppModal } from "global/components/AppModal";
import { modalStyles } from "../styles/tour.styles";
import tourTokens from "../styles/tour.tokens";
import { TOUR_MODAL_CLASS, TOUR_Z_INDEX } from "../runtime/constants";
import {
  TourChevronRightIcon,
  TourCompleteIcon,
  TourWelcomeIcon,
} from "./TourIcons";
import type { TourModalConfig } from "../types/tour.types";

const t = tourTokens.modal;

interface TourModalProps {
  open: boolean;
  variant: "welcome" | "complete";
  config: TourModalConfig;
  stepCount: number;
  onPrimary: () => void;
  onSecondary: () => void;
  onClose: () => void;
}

const TourModal = ({
  open,
  variant,
  config,
  stepCount,
  onPrimary,
  onSecondary,
  onClose,
}: TourModalProps) => {
  const titleId = `tour-${variant}-title`;
  const bodyId = `tour-${variant}-body`;
  const isWelcome = variant === "welcome";

  const footnote = config.footnote?.replace("{steps}", String(stepCount));

  return (
    <AppModal
      open={open}
      onClose={onClose}
      ariaLabel={config.title}
      /*
        The welcome and completion cards are their own thing — a centred icon
        over stacked, full-width actions — so the shell's header and footer
        slots stay empty and `modalStyles.paper` keeps drawing the card. What
        `AppModal` brings is the app's scrim, focus trap and dismiss rules,
        rather than a second Dialog wired up by hand.
      */
      hideHeader
      hideFooter
      disableBodyPadding
      scrollBody={false}
      // 420px — the width this card declared for itself before AppModal owned
      // the paper.
      size="sm"
      // `modalStyles.paper` fixes the card's width, padding and radius, which
      // would fight the phone bottom-sheet's own geometry — this card is
      // centred at every width by design.
      disableSheet
      // Above the spotlight, which sits at TOUR_Z_INDEX.
      zIndex={TOUR_Z_INDEX + 1}
      // `tour-gating` recognises this card by its class so the tour doesn't
      // treat its own modal as a blocking dialog and suppress itself.
      paperClassName={TOUR_MODAL_CLASS}
      paperSx={modalStyles.paper}
      /*
        The centred column has to live on the body, not the paper: `AppModal`
        puts a `DialogContent` between them, so the paper's `alignItems` now
        centres that full-width box rather than the icon, title and buttons
        inside it.
      */
      bodySx={modalStyles.content}
      /*
        The card already supplies its own 32px inset, and the shell's close row
        carries `24px 24px 0` of its own — stacked, that put the × 56px in
        while everything below it sat on 32. Zeroing the row puts the button
        back on the card's own grid.
      */
      headerSx={{ p: 0 }}
    >
      <Box sx={modalStyles.icon(variant)}>
        {isWelcome ? (
          <TourWelcomeIcon
            sx={{ width: t.welcomeGlyphSize, height: t.welcomeGlyphSize }}
          />
        ) : (
          <TourCompleteIcon
            sx={{ width: t.completeGlyphSize, height: t.completeGlyphSize }}
          />
        )}
      </Box>

      <Typography id={titleId} component="h2" sx={modalStyles.title}>
        {config.title}
      </Typography>

      <Typography id={bodyId} component="p" sx={modalStyles.body}>
        {config.body}
      </Typography>

      <Box sx={modalStyles.actions}>
        <Button
          disableRipple
          disableElevation
          onClick={onPrimary}
          sx={modalStyles.primaryButton}
        >
          {config.primaryLabel}
          {isWelcome && (
            <TourChevronRightIcon
              sx={{ width: t.chevronSize, height: t.chevronSize }}
            />
          )}
        </Button>
        <Button
          disableRipple
          disableElevation
          onClick={onSecondary}
          sx={modalStyles.secondaryButton}
        >
          {config.secondaryLabel}
        </Button>
      </Box>

      {footnote && (
        <Typography component="p" sx={modalStyles.footnote}>
          {footnote}
        </Typography>
      )}
    </AppModal>
  );
};

export default TourModal;
